import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';
import { clearRejectionNoticeDismissals } from '../components/common/RejectionNoticeModal';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [signedOut, setSignedOut] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = authService.getToken();
      
      if (token) {
        // Fetch full user profile with roles from server
        const profileResult = await authService.getProfile();
        
        if (profileResult.success) {
          setUser(profileResult.user);
          setIsAuthenticated(true);
        } else {
          // If profile fetch fails, clear auth
          authService.logout();
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials) => {
    setLoading(true);
    setSignedOut(false);
    // A new login is a new session: a rejection notice dismissed earlier in this tab has to
    // show up again as long as the semester is still sent back.
    clearRejectionNoticeDismissals();
    const result = await authService.login(credentials);
    
    let finalUser = null;

    if (result.success) {
      // Fetch complete user profile including roles
      const profileResult = await authService.getProfile();
      if (profileResult.success) {
        setUser(profileResult.user);
        finalUser = profileResult.user;
        setIsAuthenticated(true);
      } else {
        // Fallback to basic user data if profile fetch fails
        finalUser = {
          id: result.user.id,
          username: result.user.username,
          email: result.user.email,
          firstName: result.user.firstName,
        };
        setUser(finalUser);
        setIsAuthenticated(true);
      }
    }
    setLoading(false);
    // Return enhanced result with the fetched user profile
    return { ...result, user: finalUser || result.user };
  };

  const register = async (userData, endpoint = '/register') => {
    setLoading(true);
    const result = await authService.register(userData, endpoint);
    setLoading(false);
    return result;
  };

  const logout = () => {
    // Clear return-route intent atomically with authentication state. Router navigation
    // may render later, so the protected route must not remember the page we leave.
    setSignedOut(true);
    clearRejectionNoticeDismissals();
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const refreshProfile = async () => {
    const result = await authService.getProfile();
    if (result.success) {
      setUser(result.user);
      localStorage.setItem('currentUser', JSON.stringify(result.user));
    }
    return result;
  };

  const normalizeRoleName = (role) => {
    const raw = typeof role === 'string' ? role : role?.name || role?.authority;
    if (!raw || typeof raw !== 'string') return '';
    return raw.toUpperCase().replace(/^ROLE_/, '');
  };

  const isAdmin = () => {
    return user?.roles?.some((role) => normalizeRoleName(role) === 'ADMIN');
  };

  const validateInvitationToken = async (token) => {
    return await authService.validateInvitationToken(token);
  };
  
  const registerWithInvitation = async (token, userData) => {
    setLoading(true);
    const result = await authService.registerWithInvitation(token, userData);

    if (result.success && result.token) {
      const profileResult = await authService.getProfile();
      if (profileResult.success) {
        setUser(profileResult.user);
      } else {
        setUser(result.user);
      }
      setIsAuthenticated(true);
    }

    setLoading(false);
    return result;
  };

  const createInvitation = async (data) => {
    return await authService.createInvitation(data);
  };

  const value = {
    user,
    isAuthenticated,
    signedOut,
    loading,
    login,
    register,
    logout,
    refreshProfile,
    isAdmin,
    validateInvitationToken,
    registerWithInvitation,
    createInvitation
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
